import React, { useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import useBlogStore from '../store/useBlogStore';
import { Loader2, Calendar, User, Tag, ArrowRight, Trash2 } from 'lucide-react';

const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
        return new Date(dateString).toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        });
    } catch (e) {
        return "Invalid Date";
    }
};

const PostCard = ({ post, onDelete }) => (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transform hover:-translate-y-1 transition-transform duration-300">
        <Link to={`/blog/${post.slug}`}>
            <img
                src={post.featured_image_url}
                alt={post.title}
                className="w-full h-48 object-cover"
                onError={(e) => { e.target.src = 'https://placehold.co/600x400/e2e8f0/e2e8f0?text=Image'; }}
            />
        </Link>
        <div className="p-5">
            <div className="flex items-center text-xs text-gray-500 mb-2 space-x-3">
                <span className="flex items-center gap-1"><Calendar size={14} /> {formatDate(post.published_at)}</span>
                <span className="flex items-center gap-1"><User size={14} /> {post.author_name}</span>
            </div>
            <h2 className="text-xl font-semibold text-gray-800 mb-2 truncate" title={post.title}>
                <Link to={`/blog/${post.slug}`} className="hover:text-blue-600">{post.title}</Link>
            </h2>
            <div
                className="text-sm text-gray-600 line-clamp-3 mb-3"
                dangerouslySetInnerHTML={{ __html: post.excerpt }}
            />
            <div className="flex items-center justify-between mt-4">
                <span className="bg-blue-50 text-blue-700 px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Tag size={12} /> {post.category_name}
                </span>
                <div className="flex items-center">
                    <Link to={`/blog/${post.slug}`} className="text-sm text-blue-600 font-semibold hover:underline flex items-center gap-1">
                        Read More <ArrowRight size={14} />
                    </Link>
                </div>
            </div>
        </div>
    </div>
);

const Pagination = ({ pagination, onPageChange }) => {
    if (!pagination || pagination.totalPages <= 1) return null;

    const { page, totalPages } = pagination;

    const renderPageNumbers = () => {
        const pages = [];
        for (let i = 1; i <= totalPages; i++) {
            pages.push(
                <button
                    key={i}
                    onClick={() => onPageChange(i)}
                    className={`px-3 py-1 text-sm rounded-md ${page === i
                        ? 'bg-blue-600 text-white'
                        : 'bg-white text-gray-700 hover:bg-gray-100'
                        }`}
                >
                    {i}
                </button>
            );
        }
        return pages;
    };

    return (
        <div className="flex items-center justify-center space-x-2 mt-8">
            <button
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
                className="px-3 py-1 text-sm bg-white rounded-md disabled:opacity-50"
            >
                Prev
            </button>
            {renderPageNumbers()}
            <button
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
                className="px-3 py-1 text-sm bg-white rounded-md disabled:opacity-50"
            >
                Next
            </button>
        </div>
    );
};

const BlogListPage = () => {
    const { posts, pagination, isLoading, fetchPosts } = useBlogStore();
    const [searchParams, setSearchParams] = useSearchParams();
    const page = parseInt(searchParams.get('page') || '1', 10);
    const { deletePost } = useBlogStore();

    useEffect(() => {
        window.scrollTo(0, 0);
        fetchPosts(page);
    }, [page, fetchPosts]);

    const handlePageChange = (newPage) => {
        setSearchParams({ page: newPage });
    };

    const handleDelete = useCallback(async (post) => {
        if (!window.confirm(`Are you sure you want to delete "${post.title}"?`)) return;
        // Try to delete, then refresh posts
        if (deletePost) {
            const result = await deletePost(post.id);
            if (result && result.success) await fetchPosts(page);
        }
    }, [deletePost, fetchPosts, page]);

    return (
        <div className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 py-8">
                <h1 className="text-4xl font-bold text-center text-gray-800 mb-8">Ducktail Blog</h1>
                {isLoading && posts.length === 0 ? (
                    <div className="flex justify-center items-center py-20">
                        <Loader2 className="w-12 h-12 animate-spin text-blue-500" />
                    </div>
                ) : posts.length === 0 ? (
                    <div className="text-center py-20 text-gray-500">
                        <h2 className="text-2xl font-semibold">No posts found.</h2>
                        <p className="mt-2">Check back later for more updates!</p>
                    </div>
                ) : (
                    <>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                            {posts.map(post => <PostCard key={post.id} post={post} onDelete={handleDelete} />)}
                        </div>
                        <Pagination pagination={pagination} onPageChange={handlePageChange} />
                    </>
                )}
            </div>
        </div>
    );
};

export default BlogListPage;